// ===================================================
// ФАЙЛ: frontend/src/pages/Auth/EmailVerification.jsx
// Страница верификации email по токену
// ===================================================
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Result, Button, Spin, Typography } from 'antd';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import api from 'utils/axios';

const { Paragraph } = Typography;

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [isUserAuthorized, setIsUserAuthorized] = useState(false);
  const verificationAttemptedRef = useRef(null);

  // Проверяем авторизацию пользователя
  const userId = useSelector((state) => state.auth.user?.id);
  const token = searchParams.get('token');

  useEffect(() => {
    // Проверяем, авторизован ли пользователь
    const checkAuth = Boolean(userId);
    setIsUserAuthorized(!!checkAuth);

    if (!token) {
      setStatus('error');
      setMessage('Токен верификации не найден');
      return;
    }

    if (verificationAttemptedRef.current === token) {
      return;
    }
    verificationAttemptedRef.current = token;

    // Отправляем запрос на верификацию
    const verifyEmail = async () => {
      try {
        const response = await api.post('/api/auth/verify-email', { token });

        if (response.data.success) {
          setStatus('success');

          // Если email был уже верифицирован ранее
          if (response.data.already_verified) {
            setMessage('Email уже был верифицирован ранее');
          } else {
            setMessage('Email успешно верифицирован!');
          }

          // Если пользователь авторизован - автоматически перенаправляем в настройки через 2 секунды
          if (checkAuth) {
            setTimeout(() => {
              navigate('/settings', {
                state: { emailVerified: true },
                replace: true
              });
            }, 2000);
          }
        } else {
          setStatus('error');
          setMessage(response.data.error || 'Ошибка верификации');
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.error || 'Ошибка верификации email');
      }
    };

    verifyEmail();
  }, [token, navigate, userId]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 600,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          borderRadius: '12px'
        }}
        bodyStyle={{ padding: '40px' }}
      >
        {status === 'loading' && (
          <Result
            icon={<Spin indicator={<Loader2 size={48} className="mb-animate-spin" />} />}
            title="Верификация email..."
            subTitle="Пожалуйста, подождите, мы проверяем ваш токен верификации"
          />
        )}

        {status === 'success' && (
          <Result
            status="success"
            icon={<CheckCircle size={72} style={{ color: 'var(--ok)' }} />}
            title="Email успешно верифицирован!"
            subTitle={
              isUserAuthorized
                ? 'Через 2 секунды вы будете перенаправлены в настройки...'
                : 'Теперь вы можете войти в систему с полным доступом ко всем функциям.'
            }
            extra={
              isUserAuthorized ? [
                <Button type="primary" key="settings" onClick={() => navigate('/settings', { replace: true })}>
                  Перейти в настройки сейчас
                </Button>,
                <Button key="dashboard" onClick={() => navigate('/dashboard', { replace: true })}>
                  Панель управления
                </Button>
              ] : [
                <Button type="primary" key="login" onClick={() => navigate('/login')}>
                  Войти в систему
                </Button>,
                <Button key="home" onClick={() => navigate('/')}>
                  На главную
                </Button>
              ]
            }
          >
            {isUserAuthorized && (
              <Paragraph type="secondary" style={{ marginTop: 16 }}>
                Ваш email успешно подтвержден. Все функции платформы теперь доступны.
              </Paragraph>
            )}
          </Result>
        )}

        {status === 'error' && (
          <Result
            status="error"
            icon={<XCircle size={72} style={{ color: 'var(--err)' }} />}
            title="Ошибка верификации"
            subTitle={message}
            extra={[
              <Button type="primary" key="home" onClick={() => navigate('/')}>
                На главную
              </Button>,
              <Button key="login" onClick={() => navigate('/login')}>
                Войти
              </Button>
            ]}
          >
            <div style={{ marginTop: 16 }}>
              <Paragraph type="secondary">
                Возможные причины:
              </Paragraph>
              <ul>
                <li>Токен верификации устарел (действителен 24 часа)</li>
                <li>Email уже был верифицирован ранее</li>
                <li>Неверная ссылка верификации</li>
              </ul>
              <Paragraph type="secondary" style={{ marginTop: 16 }}>
                Если проблема сохраняется, перейдите в настройки и запросите новое письмо подтверждения.
              </Paragraph>
            </div>
          </Result>
        )}
      </Card>
    </div>
  );
};

export default EmailVerification;

